import React, { useCallback, useEffect } from 'react';
import {
	CUSTOM_LISTING_BLOCK_TYPE,
	primeListings,
	resolveBoundListing,
	subscribeListings,
	t,
} from '@bay-email/core';
import { useFocusIdx, useEditorContext } from '@bay-email/editor';
import { useSelectedNode } from '../../../../hooks/useSelectedNode';
import { useExtensionProps } from '../../../../components/Providers/ExtensionProvider';
import { SingleListingPicker } from '../SingleListing/SingleListingPicker';
import { useListingSearch, ListingItem } from '../SingleListing/useListingSearch';
import {
	ATTACHED_LISTING_PICKER_EVENT,
	collectBoundDescendantIdxs,
	consumePendingListingPickerOpen,
} from './listingAttachment';
import { get } from 'lodash';

/**
 * The listing picker for whatever owns a listing — the Listing Wrapper, or a
 * column switched to "Listing". Writes to the focused block's data.value, which is what
 * every field nested inside it resolves against.
 */
export const PinnedListingPicker: React.FC = () => {
	const [selectedListing, setSelectedListing] = React.useState<ListingItem | null>(null);
	// Bumping this opens the listing drawer (SingleListingPicker → drawer).
	const [openSignal, setOpenSignal] = React.useState(0);
	const { selectedNode } = useSelectedNode();
	const { focusIdx } = useFocusIdx();
	const {
		formHelpers,
		formState: { values },
	} = useEditorContext();
	const { isDesignAdmin } = useExtensionProps();
	const { listings, total, loading, beginSearch, setActiveView, onPaginationChange } =
		useListingSearch();

	// The binding is the id; `selectedListing` is a pre-migration leftover this
	// picker nulls on every write. Reading it alone showed "no listing selected"
	// for a block that had one as soon as the panel remounted.
	const boundValue = selectedNode?.data?.value;
	const syncFromBinding = useCallback(() => {
		setSelectedListing((resolveBoundListing(boundValue) as ListingItem) || null);
	}, [boundValue]);

	useEffect(syncFromBinding, [syncFromBinding]);
	// The id resolves through a cache that fills asynchronously — without this the
	// panel keeps showing the empty state until something else re-renders it.
	useEffect(() => subscribeListings(syncFromBinding), [syncFromBinding]);

	// Open the drawer when a nested block's "Select Listing" action retargets to
	// this block. The click happens while a child is focused, so this panel
	// mounts afterwards — consume the sticky request on mount, and also listen
	// live for the case where this block is already the focused one.
	useEffect(() => {
		if (consumePendingListingPickerOpen(focusIdx)) {
			setOpenSignal(v => v + 1);
		}
		if (typeof window === 'undefined') return;
		const handleOpen = (event: Event) => {
			const detail = (event as CustomEvent<{ idx?: string }>).detail;
			if (detail?.idx === focusIdx) setOpenSignal(v => v + 1);
		};
		window.addEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);
		return () =>
			window.removeEventListener(
				ATTACHED_LISTING_PICKER_EVENT,
				handleOpen as EventListener,
			);
	}, [focusIdx]);

	const onSelectListing = useCallback(
		(listing: ListingItem | null) => {
			setSelectedListing(listing);
			// Persist the binding only; seed the render cache with the values we
			// already hold so the canvas paints without waiting on a lookup.
			if (listing) primeListings([listing as any]);
			formHelpers.change(`${focusIdx}.data.value.selectedListingId`, listing?.id || '');
			// Clear any resolved copy a pre-migration save left on the block.
			formHelpers.change(`${focusIdx}.data.value.selectedListing`, null);

			// Children render this owner's listing, so drop any binding of their
			// own — otherwise theirs wins and they keep showing the listing this
			// pick just replaced. Runs on clear too: leaving them bound is how a
			// cleared owner still paints the old listing instead of going blank.
			const boundDescendantIdxs: string[] = [];
			collectBoundDescendantIdxs(get(values, focusIdx), focusIdx, boundDescendantIdxs);
			boundDescendantIdxs.forEach(idx => {
				formHelpers.change(`${idx}.data.value.selectedListingId`, '');
				formHelpers.change(`${idx}.data.value.selectedListing`, null);
			});
		},
		[formHelpers, focusIdx, values],
	);

	return (
		<SingleListingPicker
			listings={listings}
			selectedListing={selectedListing}
			total={total}
			size={10}
			loading={loading}
			onSelect={onSelectListing}
			onPaginationChange={onPaginationChange}
			onOpen={beginSearch}
			disabled={isDesignAdmin}
			disabledTooltip={
				selectedNode?.type === CUSTOM_LISTING_BLOCK_TYPE
					? t(
							'Users choosing this template will be able to select one listing to instantly populate all listing dynamic fields inside this wrapper.',
						)
					: t(
							'Users choosing this template will be able to select one listing to instantly populate all listing dynamic fields inside this column.',
						)
			}
			setActiveView={setActiveView}
			openSignal={openSignal}
		/>
	);
};
